import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Trash2, FileText, AlertCircle, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatJamaicaDate as formatDate } from '@/lib/datetime';

interface LearningMaterial {
  id: string;
  title: string;
  grade: number;
  subject: string;
  topic: string;
  fileUrl: string;
  processingStatus: 'pending' | 'processing' | 'ready' | 'failed';
  errorMessage?: string | null;
  createdAt: string;
  uploadedBy: string;
}

interface Grade {
  id: string;
  gradeNumber: number;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
}

interface Subject {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
}

export default function LearningMaterialsManager() {
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [grade, setGrade] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [topic, setTopic] = useState('');
  
  // Fetch all learning materials
  const { data: materials = [], isLoading } = useQuery<LearningMaterial[]>({
    queryKey: ['/api/admin/learning-materials'],
  });

  // Fetch grades and subjects from API
  const { data: grades = [] } = useQuery<Grade[]>({
    queryKey: ['/api/admin/grades'],
  });

  const { data: subjects = [] } = useQuery<Subject[]>({
    queryKey: ['/api/admin/subjects'],
  });

  // Filter for active grades and subjects, sorted by sortOrder
  const activeGrades = grades
    .filter(g => g.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const activeSubjects = subjects
    .filter(s => s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      // Use fetch directly for file uploads (apiRequest only handles JSON)
      const res = await fetch('/api/admin/learning-materials', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Upload failed');
      }
      
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/learning-materials'] });
      toast({
        title: 'Success',
        description: 'Material uploaded successfully. Text extraction is in progress.',
      });
      // Reset form
      setSelectedFile(null);
      setTitle('');
      setGrade('');
      setSubject('');
      setTopic('');
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    },
    onError: (error: any) => {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload material',
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/admin/learning-materials/${id}`);
      // DELETE returns 204 No Content, so no response body to parse
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/learning-materials'] });
      toast({
        title: 'Success',
        description: 'Material deleted successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete material',
        variant: 'destructive',
      });
    },
  });

  // Reprocess mutation
  const reprocessMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('POST', `/api/admin/learning-materials/${id}/reprocess`);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/learning-materials'] });
      toast({
        title: 'Reprocessing Started',
        description: 'Text extraction is in progress',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Reprocess Failed',
        description: error.message || 'Failed to reprocess material',
        variant: 'destructive',
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: 'Invalid File',
          description: 'Only PDF files are allowed',
          variant: 'destructive',
        });
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      // Auto-populate title from filename if empty
      if (!title) {
        const filename = file.name.replace('.pdf', '');
        setTitle(filename);
      }
    }
  };

  const handleUpload = () => {
    if (!selectedFile || !title || !grade || !subject || !topic) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all fields and select a PDF file',
        variant: 'destructive',
      });
      return;
    }

    const formData = new FormData();
    formData.append('pdf', selectedFile);
    formData.append('title', title);
    formData.append('grade', grade);
    formData.append('subject', subject);
    formData.append('topic', topic);

    uploadMutation.mutate(formData);
  };

  const getStatusBadge = (status: string, errorMessage?: string | null) => {
    switch (status) {
      case 'ready':
        return (
          <Badge variant="default" className="gap-1" data-testid={`badge-status-ready`}>
            <CheckCircle className="w-3 h-3" />
            Ready
          </Badge>
        );
      case 'processing':
      case 'pending':
        return (
          <Badge variant="secondary" className="gap-1" data-testid={`badge-status-processing`}>
            <Clock className="w-3 h-3" />
            Processing
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="gap-1" data-testid={`badge-status-failed`}>
            <AlertCircle className="w-3 h-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline" data-testid={`badge-status-unknown`}>{status}</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" data-testid="heading-materials">Learning Materials</h1>
        <p className="text-muted-foreground">
          Upload curriculum materials to improve AI question generation quality
        </p>
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle data-testid="heading-upload">Upload New Material</CardTitle>
          <CardDescription>
            Upload PDF files containing curriculum content organized by grade, subject, and topic
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                data-testid="input-title"
                placeholder="e.g., Grade 4 Math Fractions Guide"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="file">PDF File</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf"
                data-testid="input-file"
                onChange={handleFileChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="grade">Grade</Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger id="grade" data-testid="select-grade">
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  {activeGrades.map((g) => (
                    <SelectItem key={g.id} value={g.gradeNumber.toString()}>
                      {g.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger id="subject" data-testid="select-subject">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {activeSubjects.map((s) => (
                    <SelectItem key={s.id} value={s.name}>
                      {s.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="topic">Topic</Label>
              <Input
                id="topic"
                data-testid="input-topic"
                placeholder="e.g., Fractions, Verb Tenses, Plant Life Cycle"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={handleUpload}
            disabled={uploadMutation.isPending || !selectedFile}
            className="w-full md:w-auto"
            data-testid="button-upload"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Material
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Materials List */}
      <Card>
        <CardHeader>
          <CardTitle data-testid="heading-materials-list">Uploaded Materials</CardTitle>
          <CardDescription>
            {materials.length} material{materials.length !== 1 ? 's' : ''} uploaded
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8" data-testid="loading-materials">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2">Loading materials...</span>
            </div>
          ) : materials.length === 0 ? (
            <Alert data-testid="alert-no-materials">
              <FileText className="w-4 h-4" />
              <AlertDescription>
                No materials uploaded yet. Upload your first curriculum material to get started.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {materials.map((material) => (
                <div
                  key={material.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover-elevate"
                  data-testid={`material-${material.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                      <h3 className="font-medium truncate" data-testid={`text-title-${material.id}`}>
                        {material.title}
                      </h3>
                      {getStatusBadge(material.processingStatus, material.errorMessage)}
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      <span data-testid={`text-grade-${material.id}`}>Grade {material.grade}</span>
                      <span>•</span>
                      <span data-testid={`text-subject-${material.id}`}>{material.subject}</span>
                      <span>•</span>
                      <span data-testid={`text-topic-${material.id}`}>{material.topic}</span>
                      <span>•</span>
                      <span>{formatDate(material.createdAt)}</span>
                    </div>
                    {material.errorMessage && (
                      <p className="text-sm text-destructive mt-1" data-testid={`text-error-${material.id}`}>
                        {material.errorMessage}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {material.processingStatus === 'failed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reprocessMutation.mutate(material.id)}
                        disabled={reprocessMutation.isPending}
                        data-testid={`button-reprocess-${material.id}`}
                      >
                        Retry
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteMutation.mutate(material.id)}
                      disabled={deleteMutation.isPending}
                      data-testid={`button-delete-${material.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
